/**
 * @file HTTP 状态码拦截器
 * 处理特定的业务错误码和 HTTP 状态码
 */

import { Axios, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { message, Modal } from 'antd';
import { showCaptcha, subscribeCaptchaResult } from '../../biocFacade';

// 弹窗显示状态标志位，防止多次调用
const modalFlags: Record<string, boolean> = {};

/**
 * 显示 Modal，同一类型的 Modal 只显示一次
 */
function showModalOnce(key: string, modalConfig: Parameters<typeof Modal.confirm>[0]) {
    if (modalFlags[key]) {
        return; // 已经显示过，跳过
    }

    modalFlags[key] = true;

    Modal.confirm({
        ...modalConfig,
        onOk: () => {
            modalFlags[key] = false;
            modalConfig.onOk?.();
        },
        onCancel: () => {
            modalFlags[key] = false;
            modalConfig.onCancel?.();
        },
        afterClose: () => {
            // 确保关闭后重置标志位
            modalFlags[key] = false;
        }
    });
}

// 错误消息防抖 - 避免短时间内多次显示相同错误
let errorMessageTimer: NodeJS.Timeout | null = null;
let lastErrorMessage = '';
let lastErrorTime = 0;

/**
 * 显示错误消息，相同消息在短时间内只显示一次
 */
function showErrorMessage(msg: string, duration = 500) {
    const now = Date.now();

    // 如果是相同的错误消息且在500ms内，跳过
    if (msg === lastErrorMessage && now - lastErrorTime < duration) {
        return;
    }

    lastErrorMessage = msg;
    lastErrorTime = now;

    // 清除之前的定时器
    if (errorMessageTimer) {
        clearTimeout(errorMessageTimer);
    }

    message.error(msg);

    // 500ms后允许显示新的相同错误
    errorMessageTimer = setTimeout(() => {
        lastErrorMessage = '';
    }, duration);
}

function jumpToLogin() {
    window.location.href = "/pc/login"
}


let captchaShowing = false;

interface PendingRequest {
    resolve: (value: AxiosResponse) => void;
    reject: (reason?: unknown) => void;
    config: InternalAxiosRequestConfig;
}

let captchaPendingQueue: PendingRequest[] = [];

/**
 * 处理验证码结果后的队列：
 * - 成功：为每个请求打上 _retried 标记并注入 svcp_stk header 后重新发起
 * - 失败：拒绝所有挂起的请求
 *
 * 注意：axiosInstance.request() 会走完整拦截器链（包括 injectResult 的 res→res.data），
 * 但 resolve 后原始链的后续拦截器仍会继续执行。因此需要把重试结果包装回 AxiosResponse
 * 形式，避免 injectCancel/injectResult 二次处理导致数据丢失。
 */
function processCaptchaQueue(axiosInstance: Axios, stk: string | null, error?: Error) {
    const queue = captchaPendingQueue.splice(0);
    captchaShowing = false;

    if (stk !== null) {
        queue.forEach(({ resolve, reject, config }) => {
            config._retried = true;
            config.headers['svcp_stk'] = stk;
            delete config.cancelToken;
            axiosInstance.request(config).then((retryResult) => {
                // retryResult 已经过完整拦截器链（injectResult 已提取 res.data）
                // 包装回 AxiosResponse 结构，让原始链后续拦截器正确处理
                resolve({
                    data: retryResult,
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config: config,
                } as AxiosResponse);
            }).catch(reject);
        });
    } else {
        queue.forEach(({ reject }) => reject(error ?? new Error('验证失败')));
    }
}

/**
 * 状态码拦截器
 * 根据业务错误码(errno)和 HTTP 状态码进行统一处理
 */
export default function statusCodeInterceptor(axios: Axios): void {
    axios.interceptors.response.use(
        function (res: AxiosResponse) {
            // 检查业务错误码
            if (res?.data?.errno !== undefined && res.data.errno !== 0) {
                const data = res.data;

                switch (data.errno) {
                    case 120:
                        // 账号已注销
                        showErrorMessage('您的账号已注销');
                        setTimeout(() => {
                            jumpToLogin()
                        }, 3000);
                        break;
                         // 风控验证码：单独处理，需要返回 Promise 挂起请求
                    case 232:
                        // 已重试过仍返回 232，直接拒绝，防止无限循环
                        if (res.config._retried) {
                            return Promise.reject(new Error('风控验证后请求仍失败'));
                        }

                        // 将当前请求加入等待队列
                        return new Promise<AxiosResponse>((resolve, reject) => {
                            captchaPendingQueue.push({ resolve, reject, config: res.config });

                            // 并发场景：只在第一个 232 响应时展示验证码
                            if (!captchaShowing) {
                                captchaShowing = true;
                                // 先订阅结果，再展示验证码
                                // 确保 resolver 已就位，避免 onSuccess 早于订阅触发导致 Promise 永久挂起
                                subscribeCaptchaResult()
                                    .then((stk: string) => processCaptchaQueue(axios, stk))
                                    .catch((err: Error) => processCaptchaQueue(axios, null, err));
                                showCaptcha();
                            }
                        });

                    case 206:
                        // 账号被封禁
                        showErrorMessage('您的账号已被封禁');
                        setTimeout(() => {
                            jumpToLogin()
                        }, 3000);
                        break;

                    // case 401:
                    // case 4001:
                    //     // 未授权
                    //     showErrorMessage('登录已过期，请重新登录');
                    //     setTimeout(() => {
                    //         jumpToLogin()
                    //     }, 3000);
                    //     break;

                    // case 1001:
                    //     // 未登录 - 显示确认对话框（使用防重复机制）
                    //     showModalOnce('error-1001', {
                    //         title: '当前服务可能存在波动',
                    //         content: '可重新登录 或者 刷新页面',
                    //         okText: '去登录',
                    //         cancelButtonProps: { style: { display: 'none' } },
                    //         onOk: () => {
                    //             window.location.href = "/pc/login"
                    //         }
                    //     });
                    //     break;

                    default: {
                        // 其他错误 - 显示错误提示
                        // @ts-ignore - 检查是否禁用全局提示
                        const globalTipDisabled = res.config?.globalTip === false;

                        if (!globalTipDisabled) {
                            const errorMsg =
                                data.info ||
                                data.errmsg ||
                                data.message ||
                                '请求失败，请稍后重试';

                            showErrorMessage(errorMsg);
                        }
                        break;
                    }
                }
            }

            return res;
        },
        function (err) {
            // 处理 HTTP 状态码错误
            if (err?.response) {
                const status = err.response.status;
                let errorMessage = '请求失败';

                switch (status) {
                    case 400:
                        errorMessage = '请求参数错误';
                        break;
                    case 401:
                        errorMessage = '未授权，请登录';
                        // 可以跳转到登录页
                        window.location.href = '/';
                        break;
                    case 403:
                        errorMessage = '没有访问权限';
                        break;
                    case 404:
                        errorMessage = '请求的资源不存在';
                        break;
                    case 408:
                        errorMessage = '请求超时';
                        break;
                    case 500:
                        errorMessage = '服务器内部错误';
                        break;
                    case 502:
                        errorMessage = '网关错误';
                        break;
                    case 503:
                        errorMessage = '服务暂时不可用';
                        break;
                    case 504:
                        errorMessage = '网关超时';
                        break;
                    default:
                        errorMessage = `请求失败 (${status})`;
                }

                err.message = errorMessage;
                showErrorMessage(errorMessage);
            } else if (err.message === 'Network Error') {
                // 网络错误
                showErrorMessage('网络连接失败，请检查网络');
            } else if (err.code === 'ECONNABORTED') {
                // 请求超时
                showErrorMessage('请求超时，请稍后重试');
            }

            return Promise.reject(err);
        }
    );
}
